import React, { useEffect, useState } from "react";
import { WorkStateType } from "common/services/microservices/basic-client";
import { authenticatedClient } from "core/components/AuthProvider";
import { MicroservicesEndpoint } from "common/services/ApiServerService";
import {
  RevenueBookingClient,
  ICashRecievedReportFilterRequest,
  CashRecievedReportFilterRequest,
  ISimpleSaleFilterRequest,
  ICashReportFilterOptionsC,
  SimpleSaleFilterRequest,
  CashReportFilterOptionsC,
  PaymentPivotType,
} from "common/services/microservices/finance-client";
import { Button, Checkbox, Col, Divider, Row, Select } from "antd";
import moment from "moment";
import { ProgressDisplayer } from "./ProgressDisplayer";
import { FullReportRequestBuilder } from "./FullReportRequestBuilder";
import { useWorkWaiter } from "common/components/useWorkWaiter";
import { DateRangePicker } from "./SaleDatePickers";

function FullReportGenerator(props: { request: ICashRecievedReportFilterRequest }) {
  const { startWork, isLoading, taskState, taskEvents, progress, downloadResult } = useWorkWaiter();

  async function startReport() {
    const bookingClient = await authenticatedClient(MicroservicesEndpoint.finance, RevenueBookingClient);
    return await bookingClient.startGenerateFullCashReceivedReportWork(
      CashRecievedReportFilterRequest.fromJS(props.request)
    );
  }

  return (
    <>
      <Button
        loading={isLoading}
        onClick={() => {
          startWork(
            startReport,
            `RevenueReport-CashReceived-${moment().format("YYYYMMDD")}.zip`,
            "Started generating report. This may take some time."
          );
        }}
      >
        Generate New Cash Received Report
      </Button>
      <div className="my-2">
        <ProgressDisplayer progress={progress} taskEvents={taskEvents} state={taskState} />
      </div>
      {taskState === WorkStateType.Success && (
        <Button loading={isLoading} className="ml-3 mt-2 btn-success" onClick={downloadResult}>
          Download Reports
        </Button>
      )}
    </>
  );
}

interface CashReceivedReportFiltersProps {
  onFiltersChange: (request: ICashReportFilterOptionsC, ok: boolean) => void;
}

const AllGlAccountIds = [1710, 1725, 4715, 4000];

function CashReceivedReportFilters(props: CashReceivedReportFiltersProps) {
  const { onFiltersChange } = props;

  const [filterDates, setFilterDates] = useState<boolean>(false);

  const [data, setData] = useState<ICashReportFilterOptionsC>({
    pivot_date_type: PaymentPivotType.PaymentDate,
    gl_account_ids: AllGlAccountIds,
    pivot_date_start: undefined,
    pivot_date_end: undefined,
  });

  useEffect(() => {
    let ok = (data.gl_account_ids?.length ?? 0) > 0 && data.pivot_date_type !== undefined;

    if (filterDates) {
      ok = ok && (data.pivot_date_start !== undefined || data.pivot_date_end !== undefined);
    }

    onFiltersChange(data, ok);
  }, [data, filterDates]);

  return (
    <>
      <Row justify="space-between" align="bottom">
        <Col span={7}>
          <Col>GL Categories</Col>
          <Select<number[]>
            style={{ width: "100%" }}
            mode="multiple"
            value={data.gl_account_ids}
            onChange={(value) => setData({ ...data, gl_account_ids: value })}
            options={AllGlAccountIds.map((id) => ({ value: id, label: id.toString() }))}
          />
        </Col>
        <Col span={7}>
          <Col>Pivot Date Option</Col>
          <Select<PaymentPivotType>
            style={{ width: "100%" }}
            value={data.pivot_date_type}
            onChange={(value) => setData({ ...data, pivot_date_type: value })}
            options={[
              { value: PaymentPivotType.PaymentDate, label: "Payment Date" },
              { value: PaymentPivotType.PaymentEffectiveDate, label: "Payment Effective Date" },
            ]}
          />
        </Col>
        <Col span={7} style={{ justifySelf: "center" }}>
          <Checkbox checked={filterDates} onChange={(e) => setFilterDates(e.target.checked)}>
            Limit Pivot Dates
          </Checkbox>
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col offset={16} span={8}>
          {filterDates && (
            <DateRangePicker
              label="Pivot Date Range"
              filterDates={filterDates}
              startDate={data.pivot_date_start ? moment(data.pivot_date_start) : undefined}
              endDate={data.pivot_date_end ? moment(data.pivot_date_end) : undefined}
              setStartDate={(date) => setData({ ...data, pivot_date_start: date ? date.toDate() : undefined })}
              setEndDate={(date) => setData({ ...data, pivot_date_end: date ? date.toDate() : undefined })}
            />
          )}
        </Col>
      </Row>
    </>
  );
}

export function FullReportGeneratorCashReceived() {
  const [simpleRequest, setSimpleRequest] = useState<ISimpleSaleFilterRequest>();
  const [cashRequest, setCashRequest] = useState<ICashReportFilterOptionsC>();
  const [cashFilterOk, setCashFilterOk] = useState<boolean>();
  const [simplefilterOK, setSimpleFilterOk] = useState<boolean>();

  return (
    <div style={{ textAlign: "center", marginBottom: "20px" }}>
      <h3>Cash Received Report Generator</h3>
      <h5>This report will pull the cash received for the current sale data.</h5>
      <Divider />
      <h5>Cash Received Filters</h5>
      <CashReceivedReportFilters
        onFiltersChange={(request, ok) => {
          setCashRequest(request);
          setCashFilterOk(ok);
        }}
      />
      <Divider />
      <h5>Standard Filters</h5>
      <FullReportRequestBuilder
        hideTableFilter={true}
        showForecastFilter={false}
        onFiltersChange={(request, ok) => {
          setSimpleRequest(request);
          setSimpleFilterOk(ok);
        }}
      />
      {simpleRequest && cashRequest && cashFilterOk && simplefilterOK && (
        <>
          <Divider />
          <FullReportGenerator
            request={
              new CashRecievedReportFilterRequest({
                simple_sale_filter_request: new SimpleSaleFilterRequest(simpleRequest),
                cash_report_filter_options: new CashReportFilterOptionsC(cashRequest),
              })
            }
          />
        </>
      )}
    </div>
  );
}
